<template>

  <div class="report-wrapper">
    <template v-if="!report.policy_service">

      <div class="report">
        <div class="report__data report__data--small">
          {{ formatDisplayDate(report.date) }}
        </div>
        <div class="report__data report__data--medium">{{ report.agent }}</div>
        <div class="report__data report__data--medium"> <template v-if="report.type">
            {{ report.type.name }}
          </template></div>
        <div class="report__data report__data--large">
          {{ report.description }}
        </div>
        <!-- <div class="report__data report__data--small">{{ storno }} CHF</div> -->
        <div class="report__data report__data--medium">
          {{ isOpened === 0 ? storno : balance }}
          CHF
        </div>

        <div v-if="report.payout_id != null && report.type_id == 9"
          class="report__data report__data--last icon icon__print" @click="openReport()">
        </div>
        <div v-if="!report.payout_id && report.type.is_manual" class="report__data report__data--last icon icon__delete"
          @click="deleteReport(report.uuid)">
        </div>
      </div>
    </template>
    <div class="report" v-else>
      <div class="report__data report__data--medium">
        {{ formatDisplayDate(report.date) }}
      </div>
      <div class="report__data report__data--large">
        {{ customerName }}
      </div>
      <div class="report__data report__data--large">
        {{ report.company }}
      </div>
      <div class="report__data report__data--medium">
        {{ insuranceType }}
      </div>
      <div class="report__data report__data--medium">
        {{ report.policy_number }}
      </div>
      <div class="report__data report__data--medium">
        {{ report.agent }}
      </div>
      <div class="report__data report__data--medium">
        <template v-if="report.type">
          {{ report.type.name }}
        </template>

      </div>
      <!-- <div class="report__data report__data--medium">{{ storno }} CHF</div> -->
      <div class="report__data report__data--medium">
        <p style="text-align: center">{{ isOpened === 0 ? storno : balance }}
        CHF</p>
      </div>

      <router-link class="report__data report__data--last icon icon__right-arrow" :to="{
        name: 'AccountDetails',
        params: {
          entity: 'customer',
          id: report.customer.id,
        },
      }"></router-link>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import api from "@/api";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import statics from "@/statics";

defineOptions({
  name: "Report"
});

const props = defineProps({
  report: {
    required: true,
    type: Object,
  },
  teamId: {
    required: true,
    type: Number,
  },
});

const store = useStore();

// Computed Properties
const customerName = computed(() => {
  if (props.report.customer) {
    if (props.report.customer.company_name) {
      return props.report.customer.company_name;
    }
    return `${props.report.customer.first_name} ${props.report.customer.last_name}`;
  }
  return statics.empty;
});

const insuranceType = computed(() => {
  if (props.report.policy_service) {
    return props.report.policy_service.length
      ? props.report.policy_service[0].branch_category_service.code
      : statics.empty;
  }
  return statics.empty;
});

const storno = computed(() => {
  if (props.report.policy_service) {
    return props.report.policy_service.length
      ? props.report.policy_service[0].policy_service_payment.agent
      : statics.empty;
  } else {
    return props.report.debit || statics.empty;
  }
});

const isOpened = computed(() => {
  if (props.report.policy_service) {
    for (let service of props.report.policy_service) {
      if (service.policy_service_payment) {
        for (let payment of service.policy_service_payment) {
          if (!payment.paid) return false;
        }
      } else {
        return false;
      }
    }
    return true;
  }
  return false;
});

const balance = computed(() => props.report.balance);

// Methods
const deleteReport = (id) => {
  store.commit("setDeleteModal", {
    display: true,
    message: '',
    action: () => deleteManualReport(id),
    id: id,
  });
};

const deleteManualReport = async (id) => {
  try {
    await api({ requiresAuth: true }).delete(`/v1/transactions/${id}`);
    await store.dispatch("getReports", props.teamId);
  } catch (err) {
    console.error(err);
  }
};

const openReport = async () => {
  try {
    const response = await api({ requiresAuth: true }).get(
      `/v1/user-payouts/${props.report.payout_id}/pdf`,
      { responseType: "blob" }
    );

    if (!response.data || response.data.size === 0) {
      throw new Error("Empty PDF response received");
    }

    const contentType = response.headers['content-type'] || 'application/pdf';
    const blob = new Blob([response.data], { type: contentType });
    const fileURL = window.URL.createObjectURL(blob);

    if (contentType.includes("pdf") || contentType.includes("image/")) {
      const { width, height } = screen;
      window.open(
        fileURL,
        "_blank",
        `location=yes,width=${width},height=${height},scrollbars=yes,status=yes`
      );
    } else {
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", "agent_payout.pdf");
      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
    }

    window.URL.revokeObjectURL(fileURL);
  } catch (err) {
    console.error("PDF download error:", err);
    const errorMessage = err.response?.data instanceof Blob
      ? "Server error while generating PDF"
      : (err.response?.data?.message || "Error downloading PDF. Please try again.");

    store.commit("setToast", {
      display: true,
      type: "error",
      message: errorMessage
    });
  }
};
</script>

<style scoped lang="scss">
.report {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  border-radius: var(--radius-1);
  background: var(--bg-1);
  box-shadow: 0px 0px 15px var(--shadow-4);
  position: relative;
  z-index: 1;

  &__data {
    border-right: 1px solid var(--border-12);
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 15px 10px;
    color: var(--text-9);

    &--small,
    &--last {
      flex: 1;
    }

    &--medium {
      flex: 2;
    }

    &--large {
      flex: 3;
    }

    &--last {
      max-width: 45px;
      border-right: 0;
    }

    &:last-child {
      border-right: 0;
    }
  }
}

.report-wrapper {
  width: 100%;
}
</style>
